/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Ticket, TicketFields } from '@touchplan/common';

import { dateCache } from '@touchplan/common';

import * as _ from 'lodash-es';

import { ILoggerService, DefaultLoggerService } from '@touchplan/logging';

export interface IdMapConfig {
	roleId?: { [key: string]: string };
	assignedProjectMemberId?: { [key: string]: string };
	assignedProjectMemberRoleId?: { [key: string]: string };
	creatorId?: { [key: string]: string };
	requestedByProjectMemberId?: { [key: string]: string };
	requestedByProjectMemberRoleId?: { [key: string]: string };
	responsibleProjectMemberId?: { [key: string]: string };
	responsibleProjectMemberRoleId?: { [key: string]: string };
	locationId?: { [key: string]: string };
	priorityId?: { [key: string]: string };
	projectMemberId?: { [key: string]: string };
	procoreRfiId?: { [key: string]: string };
	varianceId?: { [key: string]: string }; //special handling
}

export interface SrcData {
	activeLineX: number;
	activeLineDate: string;
	planId: string;
	projectId: string;
}

const idMapKeys = [
	'roleId',
	'assignedProjectMemberId',
	'assignedProjectMemberRoleId',
	'creatorId',
	'requestedByProjectMemberId',
	'requestedByProjectMemberRoleId',
	'responsibleProjectMemberId',
	'responsibleProjectMemberRoleId',
	'locationId',
	'priorityId',
	'procoreRfiId',
	// 'isAccepted'
];

const statusKeys = [
	'actualCrewSize',
	'actualDurationDays',
	'actualStart',
	'actualFinish',
	'actualWorkingDays',
	'isActualized',
	'lastPlannedCrewSize',
	'lastPlannedDurationDays',
	'lastPlannedFinish',
	'lastPlannedStart',
	'lastPlannedWorkingDays',
	'promisePeriodStack',
	'promisePeriodPosition',
];

function isTicketArray(arg: any): arg is Ticket[] {
	return arg && arg[0] && arg[0].$id !== undefined;
	// return arg.$id !== undefined;
}

function isMap<T>(arg: any): arg is Map<string, T> {
	return arg.size !== undefined;
}

export type SupportiveTicketArray =
	| Ticket[]
	| Map<string, Ticket>
	| TicketFields[]
	| Map<string, TicketFields>;

/** @deprecated This may still be used by CS, but the Clipboard UI is replacing it... */
export class TicketCopy {
	private srcTickets: TicketFields[];
	private timelineTicketExistanceMap: Map<string, any>;
	private srcData: SrcData;
	private logger: ILoggerService;

	constructor() {
		this.logger = new DefaultLoggerService();
		this.logger.setContext('TicketCopy');
	}

	// copy, save the .srcTickets somewhere, paste, wait a bit for fbdb to do things, copy the pastedTickets,
	// call compareDatas with the old data and the new data
	//...this isn't going to be helpful on second thought, too much data changing on copys
	public compareDatas(passedIn: TicketFields[], local?: TicketFields[]) {
		let allGood = true;
		const fields = [
			'activityName',
			'actualFinish',
			'createdAt',
			'crewSize',
			'dateRequested',
			'isActualized',
			'isLateConstraintRequested',
			'isLateTicketFinish',
			'isOutOfOrder',
			'lastPlannedFinish',
			'lastPlannedStart',
			'notes',
			'plannedFinish',
			'plannedStart',
			'promisePeriodPosition',
			'type',
		];
		if (!local) {
			local = this.srcTickets;
		}

		if (passedIn.length !== local.length) {
			this.logger.log('length mismatch');
			return false;
		}

		fields.forEach((fieldName) => {
			passedIn.forEach((ticketData, idx) => {
				if (ticketData[fieldName] !== undefined && ticketData[fieldName] !== local[idx][fieldName]) {
					this.logger.log('mismatch for fieldName ', fieldName, ' at idx ', idx, ticketData, local[idx]);
					allGood = true;
				}
			});
			local.forEach((ticketData, idx) => {
				if (ticketData[fieldName] !== undefined && ticketData[fieldName] !== passedIn[idx][fieldName]) {
					this.logger.log('mismatch for fieldName ', fieldName, ' at idx ', idx, ticketData, local[idx]);
					allGood = true;
				}
			});
		});
		return allGood;
	}

	private getTicketDataArray(ticketList: SupportiveTicketArray) {
		let processedArray: TicketFields[];

		//handle maps or arrays
		let ticketArray: Ticket[] | TicketFields[];
		if (isMap<Ticket | TicketFields>(ticketList)) {
			ticketArray = Array.from((ticketList as any).values());
		} else {
			ticketArray = ticketList;
		}

		//handle tickets or ticketFields
		if (isTicketArray(ticketArray)) {
			this.logger.log('isTicketArray');
			processedArray = ticketArray.map((t) => t.rawDatabase);
		} else {
			processedArray = ticketArray;
		}
		return processedArray;
	}

	//technically supports checking both sides, but in theory there's no good reason why we shouldn't be able to overspecify
	// the passed in config. For now consider A to be the calculated map and b to be the passed in map
	private compareMaps(mapA: IdMapConfig, mapB: IdMapConfig) {
		if (!mapA || !mapB) {
			return false;
		}

		const checkOneSide = (mapA: IdMapConfig, mapB: IdMapConfig) => {
			for (let key in mapA) {
				// should probably use (!(key in mapB) for future stuff, unwilling to modify this code because it's not _exactly_
				// the same
				// if (!(key in mapB)) {
				if (!(mapB as any)[key]) {
					this.logger.log('missing key', key);
					return false;
				}
				for (let id in (mapA as any)[key]) {
					if (!(mapB as any)[key][id]) {
						this.logger.log('missing key', key, id);
						return false;
					}
				}
			}
			return true;
		};

		return checkOneSide(mapA, mapB); // && checkOneSide(mapB, mapA);
	}

	private iterVariance(ticket: TicketFields, cb: (varianceId: string, pp: any) => void) {
		if (ticket.promisePeriodStack) {
			for (let ppId in ticket.promisePeriodStack) {
				if (ticket.promisePeriodStack[ppId] && ticket.promisePeriodStack[ppId].varianceId) {
					cb(ticket.promisePeriodStack[ppId].varianceId, ticket.promisePeriodStack[ppId]);
				}
			}
		}
	}

	//also roles now
	public expandProjectMember(config: IdMapConfig) {
		if (config && config.projectMemberId) {
			config.assignedProjectMemberId = config.projectMemberId;
			config.requestedByProjectMemberId = config.projectMemberId;
			config.responsibleProjectMemberId = config.projectMemberId;
			config.creatorId = config.projectMemberId;

			config.assignedProjectMemberRoleId = config.roleId;
			config.responsibleProjectMemberRoleId = config.roleId;
			config.requestedByProjectMemberRoleId = config.roleId;
		}
		return config;
	}
	public joinProjectMember(config: IdMapConfig) {
		if (!config) {
			return config;
		}
		//projectMembers
		config.projectMemberId = {
			...(config.assignedProjectMemberId || {}),
			...(config.requestedByProjectMemberId || {}),
			...(config.responsibleProjectMemberId || {}),
			...(config.creatorId || {}),
		};
		delete config.assignedProjectMemberId;
		delete config.requestedByProjectMemberId;
		delete config.responsibleProjectMemberId;
		delete config.creatorId;

		//roles
		config.roleId = {
			...(config.roleId || {}),
			...(config.assignedProjectMemberRoleId || {}),
			...(config.responsibleProjectMemberRoleId || {}),
			...(config.requestedByProjectMemberRoleId || {}),
		};
		delete config.assignedProjectMemberRoleId;
		delete config.responsibleProjectMemberRoleId;
		delete config.requestedByProjectMemberRoleId;

		return config;
	}

	public collectRequiredTicketMappings(ticketList: SupportiveTicketArray) {
		const processedArray = this.getTicketDataArray(ticketList);
		const collection: IdMapConfig = {};

		processedArray.forEach((ticket) => {
			idMapKeys.forEach((key) => {
				if (!ticket[key]) {
					return;
				}
				if (!(collection as any)[key]) {
					(collection as any)[key] = {};
				}
				(collection as any)[key][ticket[key]] = 'placeholder-' + ticket[key];
			});

			//variance
			this.iterVariance(ticket, (varianceId, pp) => {
				let key = 'varianceId';
				if (!(collection as any)[key]) {
					(collection as any)[key] = {};
				}
				(collection as any)[key][varianceId] = 'placeholder-' + varianceId;
			});
		});

		return collection;
	}

	public copyTickets(
		ticketList: SupportiveTicketArray,
		timelineTicketList: Map<string, any>,
		activeLineX: number,
		activeLineDate: string,
		planId: string,
		projectId: string
	) {
		//placeholder validation
		if (
			!ticketList ||
			!timelineTicketList ||
			activeLineX === undefined ||
			!activeLineDate ||
			!planId ||
			!projectId
		) {
			this.logger.log('all args needed', arguments);
			return;
		}

		this.srcTickets = this.getTicketDataArray(ticketList);
		this.timelineTicketExistanceMap = timelineTicketList;
		this.srcData = {
			activeLineX,
			activeLineDate,
			planId,
			projectId,
		};

		if (!this.srcTickets || !this.srcTickets.length) {
			this.logger.log('no tickets were copied');
			return;
		}

		this.logger.log('copied ' + this.srcTickets.length + ' tickets');
	}

	public numberOfCopiedTickets(): number {
		if (!this.srcTickets || !this.srcTickets.length) {
			return 0;
		}
		return this.srcTickets.length;
	}

	//notes:
	// - in order to implement same plan copying, the ticketId's need squizzling, this will require passing in an id generator
	// - should switch to a deep copy when cloning srcData for dependency data mostly
	public pasteTickets(
		activeLineX: number,
		activeLineDate: string,
		planId: string,
		startingY: number,
		startingX: number,
		ticketIdGetter: () => string,
		config: IdMapConfig,
		bypassValidation = false
	) {
		if (!this.srcTickets || !this.srcData) {
			this.logger.log('no src data to copy from');
			return;
		}
		// if(this.srcData.activeLineDate > activeLineDate){ this.logger.log('can only copy tickets from a plan with an active line equal to or before the target plan'); return; }

		if (!config) {
			config = {};
		}

		this.expandProjectMember(config);
		const xOffset = activeLineX - this.srcData.activeLineX;
		const calculatedSrcMappings = this.collectRequiredTicketMappings(this.srcTickets);

		if (!bypassValidation && !this.compareMaps(calculatedSrcMappings, config)) {
			this.logger.log('the provided config is incomplete, aborting...');
			this.logger.log('original', calculatedSrcMappings, 'provided', config);
			return;
		}

		//for copying into, so that paste can be run multiple times off the same src data
		const writeTicketList: TicketFields[] = [];

		const pullList: TicketFields[] = [];
		const activeList: TicketFields[] = [];

		let topY = Infinity;
		let lowestPullX = Infinity;

		//map ticketid to array index for quicker access
		const ticketCache: any = {};
		const oldToNewId: any = {};

		const stats = {
			pullToPull: 0,
			pullToActive: 0,
			activeToActive: 0,
			activeToPull: 0,
		};

		//initial iteration
		this.srcTickets.forEach((rawTicketData, idx) => {
			const ticket = _.clone(rawTicketData);
			ticket.id = ticketIdGetter();

			ticketCache[ticket.id] = ticket;
			oldToNewId[rawTicketData.id] = ticket.id;

			for (let key in config) {
				if (ticket[key] && (config as any)[key][ticket[key]]) {
					ticket[key] = (config as any)[key][ticket[key]];
				}
			}

			this.iterVariance(ticket, (varianceId, pp) => {
				let key = 'varianceId';
				if ((config as any)[key] && (config as any)[key][varianceId]) {
					pp.varianceId = (config as any)[key][varianceId];
				}
			});

			if (ticket.top < topY) {
				topY = ticket.top;
			}

			const finish = ticket.plannedFinish || ticket.lastPlannedFinish;
			//pull
			if (!finish || finish > activeLineDate) {
				if (ticket.left < lowestPullX) {
					lowestPullX = ticket.left;
				}
				pullList.push(ticket);
			}
			//active
			else {
				activeList.push(ticket);
			}

			// ticket.left = ticket.left + xOffset;
			// writeTicketList.push(ticket);
		});
		if (lowestPullX === Infinity) {
			lowestPullX = startingX;
		} //shouldn't matter, Infinity means no pull tickets

		const yOffset = startingY - topY;
		const pullXOffset = startingX - lowestPullX;

		//do a second iteration for dependencies to ease adding ticketId swizzling later
		//also to adjust the top value
		activeList.forEach((ticket) => {
			const start = ticket.plannedStart || ticket.lastPlannedStart;
			const activeLineDayOffset = dateCache.diffDays(activeLineDate, start) + 1;
			ticket.left = activeLineX - 145 * activeLineDayOffset; // - ticket.width;
			ticket.height = 135;

			ticket.top = ticket.top + yOffset;
			// ticket.left = ticket.left + xOffset;

			if (ticket.isActive) {
				stats.activeToActive++;
			} else {
				stats.activeToPull++;
			}

			//actually need to do date math now to account for the pull tickets being converted into actuals

			dependencyStuff(ticket, this);
		});

		pullList.forEach((ticket) => {
			ticket.top = ticket.top + yOffset;
			ticket.left = ticket.left + pullXOffset;
			ticket.width = 290;
			ticket.height = 270;

			if (ticket.isActive) {
				stats.pullToActive++;
			} else {
				stats.pullToPull++;
			}

			dependencyStuff(ticket, this);
		});

		function dependencyStuff(ticket: TicketFields, self: any) {
			['predecessors'].forEach((depKey) => {
				if (!ticket[depKey]) {
					return;
				}

				//strip other plans
				if (ticket[depKey][self.srcData.planId]) {
					ticket[depKey] = { [planId]: ticket[depKey][self.srcData.planId] };

					const ticketSection = ticket[depKey][planId];

					//now strip tickets that don't exist
					const keepers: any = {};
					let anyKeepers = false;
					for (let tDep in ticketSection) {
						//swizzle here
						if (ticketCache[oldToNewId[tDep]] !== undefined) {
							keepers[oldToNewId[tDep]] = ticketSection[tDep];
							anyKeepers = true;
						}
					}

					ticket[depKey][planId] = keepers; //replace planId
					if (!anyKeepers) {
						delete ticket[depKey];
					}
				} else {
					delete ticket[depKey];
				}
			});
		}

		this.logger.log('paste stats', stats);
		return [...activeList, ...pullList];
	}

	public stripStatus() {
		if (!this.srcTickets || !this.srcTickets.length) {
			this.logger.log('no tickets have been copied');
			return;
		}
		this.srcTickets.forEach((ticket) => {
			this.stripTicketStatus(ticket);
		});
	}

	public stripTicketStatus(ticket: TicketFields) {
		if (!ticket.plannedStart && ticket.lastPlannedStart) {
			ticket.plannedStart = ticket.lastPlannedStart;
		}
		if (!ticket.plannedFinish && ticket.lastPlannedFinish) {
			ticket.plannedFinish = ticket.lastPlannedFinish;
		}
		statusKeys.forEach((key) => {
			delete ticket[key];
		});
	}
}

/** @deprecated This may still be used by CS, but the Clipboard UI is replacing it... */
export const ticketCopy = new TicketCopy();
window.ticketCopy = ticketCopy;
